import React from 'react';

import { Header, Container, Price, SmallPrint } from './PaymentInfo.styled';
import { Text } from 'components/v2/Typography/Text';
import { usePatient } from 'hooks/usePatient/usePatient';
import { isAppointmentType, utils, AppointmentType } from 'kb-shared';
import theme from 'kb-shared/theme';
import { isCoachingAppointment } from 'kb-shared/utilities/appointment.helper';
import { hasWalmartMembership } from 'utilities/patient';

const { isGYNAppointment } = utils;

// Payment status messages
const FEE_WAIVED = 'Fee Waived';
const FREE_OF_CHARGE = 'Free Of Charge';

// Standard appointment messages
const PAYMENT_DUE_HEADER = 'Payment Due at Appointment*';
const PAYMENT_DUE_FOOTNOTE =
  '*Subject to insurance coverage. If not covered, you will be responsible for the total + lab work.';

// Virtual NPV appointment messages
const VIRTUAL_NPV_DESCRIPTION =
  'No charges will occur until the time of your appointment. We accept most insurances and HSA/FSA for eligible services. If you do not have insurance, the cost for your first visit will be $';
const VIRTUAL_NPV_DESCRIPTION_END = ', due at the time of your appointment.';
const WALMART_NPV_DESCRIPTION =
  'No charges will occur until the time of your appointment. We accept most insurances and HSA/FSA for eligible services. Your appointment may be subject to a specialist office visit co-pay under your Walmart-sponsored insurance plan.';

// Coaching appointment messages
const CREDIT_CARD_INFO =
  'We accept credit card, HSA/FSA. Have an employer benefit with wellness coverage? Call us today to schedule.';

type PaymentDisplayType =
  | 'fee-waived'
  | 'free'
  | 'no-display'
  | 'new-patient-visit'
  | 'standard-appointment'
  | 'coaching';

export type Props = {
  appointment: AppointmentType | null;
  discountedPrice?: number;
  hasEmployerMembership?: boolean;
};

/**
 * Main PaymentInfo component that renders different payment information
 * based on appointment type and pricing
 */
export const PaymentInfo = ({ appointment, discountedPrice, hasEmployerMembership }: Props) => {
  if (!appointment) return null;
  if (!shouldShowPaymentInfo(appointment)) return null;

  const displayType = getPaymentDisplayType(appointment, discountedPrice);

  switch (displayType) {
    case 'no-display':
      return null;
    case 'fee-waived':
      return <Price>{FEE_WAIVED}</Price>;
    case 'free':
      return <Price>{FREE_OF_CHARGE}</Price>;
    case 'new-patient-visit':
      return (
        <NewPatientVisitPaymentInfo appointment={appointment} discountedPrice={discountedPrice} />
      );
    case 'standard-appointment':
      return <StandardAppointmentPaymentInfo />;
    case 'coaching':
      return (
        <CoachingPaymentInfo
          appointment={appointment}
          discountedPrice={discountedPrice}
          hasEmployerMembership={hasEmployerMembership}
        />
      );
    default:
      return null;
  }
};

/**
 * Determines the display type based on appointment properties and pricing
 */
const getPaymentDisplayType = (
  appointment: AppointmentType | null,
  discountedPrice?: number
): PaymentDisplayType => {
  if (!appointment) return 'no-display';

  const isAppointmentFree = appointment.rateCents === 0;

  if (discountedPrice === 0) return 'fee-waived';
  if (isAppointmentFree) return 'free';

  const isCoaching = isCoachingAppointment(appointment);
  const shouldChargeAtBookingForHolisticHealth = appointment.shouldChargeAtBooking;

  // Coaching appointment logic
  if (isCoaching && !shouldChargeAtBookingForHolisticHealth) return 'no-display';

  if (appointment.newPatientVisit) return 'new-patient-visit';
  if (isCoaching) return 'coaching';

  return 'standard-appointment';
};

/**
 * Calculates the final price considering discounts
 */
const calculatePrice = (appointment: AppointmentType, discountedPrice?: number): number => {
  return (discountedPrice || appointment.rateCents) / 100;
};

/**
 * Determines if payment info should be displayed for the given appointment
 */
const shouldShowPaymentInfo = (appointment: AppointmentType | null): boolean => {
  if (!isAppointmentType(appointment)) return false;

  const appointmentCategoryId = parseInt(appointment.category.id);
  if (isGYNAppointment(appointmentCategoryId)) return false;

  return true;
};

/**
 * Renders payment information for NPV appointments
 */
const NewPatientVisitPaymentInfo = ({
  appointment,
  discountedPrice
}: {
  appointment: AppointmentType;
  discountedPrice?: number;
}) => {
  const { patient } = usePatient();
  const hasWalmartBenefits = hasWalmartMembership(patient);

  const price = calculatePrice(appointment, discountedPrice);

  return (
    <Container>
      <SmallPrint>
        <strong>
          {hasWalmartBenefits ? (
            WALMART_NPV_DESCRIPTION
          ) : (
            <>
              {VIRTUAL_NPV_DESCRIPTION}
              {price}
              {VIRTUAL_NPV_DESCRIPTION_END}
            </>
          )}
        </strong>
      </SmallPrint>
    </Container>
  );
};

/**
 * Renders payment information for standard appointments
 */
const StandardAppointmentPaymentInfo = () => (
  <Container>
    <Header>{PAYMENT_DUE_HEADER}</Header>
    <SmallPrint>{PAYMENT_DUE_FOOTNOTE}</SmallPrint>
  </Container>
);

/**
 * Renders payment information for coaching appointments
 */
const CoachingPaymentInfo = ({
  appointment,
  discountedPrice,
  hasEmployerMembership
}: {
  appointment: AppointmentType;
  discountedPrice?: number;
  hasEmployerMembership?: boolean;
}) => {
  const price = calculatePrice(appointment, discountedPrice);
  const showCreditCardHSAOrFSAInfo = !hasEmployerMembership;

  return (
    <Container>
      <Price>{`$${price} due today`}</Price>
      {showCreditCardHSAOrFSAInfo && (
        <Text color={theme.colors.white} size="sm">
          {CREDIT_CARD_INFO}
        </Text>
      )}
    </Container>
  );
};
